import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../config";
import Loader from "../components/Loader";

const Pages = () => {
  const [pages, setPages] = useState([]);
  const [usernames, setUsernames] = useState({});
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      window.location.href = "/login";
      return;
    }
  }, []);

  useEffect(() => {
    const fetchPages = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}api/pages/find-all`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        setPages(response.data);
        fetchUsernames(response.data);
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || "Failed to fetch pages", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchPages();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredPages = pages.filter((page) =>
    (page.pagename?.toLowerCase() || "").includes(search.toLowerCase()) ||
    (page.content?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const sortedPages = [...filteredPages].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valA = a[sortConfig.key]?.toString().toLowerCase() || "";
    const valB = b[sortConfig.key]?.toString().toLowerCase() || "";
    return sortConfig.direction === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  const totalPages = Math.ceil(sortedPages.length / pageSize);
  const paginatedPages = sortedPages.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus ? 0 : 1;
    Swal.fire({
      title: "Are you sure you want to update status?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");
          await axios.put(`${BASE_URL}api/pages/update/${id}`, { active: newStatus }, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          setPages((prevPages) => prevPages.map((page) => (page.id === id ? { ...page, active: newStatus } : page)));
          Swal.fire("Updated!", "The page has been updated.", "success");
        } catch (error) {
          Swal.fire("Error", error.response?.data?.message || "Failed to update page status", "error");
        }
      }
    });
  };

  const deletePage = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");
          await axios.delete(`${BASE_URL}api/pages/delete/${id}`, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          setPages((prevPages) => prevPages.filter((page) => page.id !== id));
          Swal.fire("Deleted!", "The page has been deleted.", "success");
        } catch (error) {
          Swal.fire("Error", error.response?.data?.message || "Failed to delete page", "error");
        }
      }
    });
  };

  // Function to fetch usernames based on user IDs (parent_id, addedby, modifiedby)
  const fetchUsernames = async (pages) => {
    const token = localStorage.getItem("token");
    const userIds = [
      ...new Set(
        pages.flatMap((page) => [page.parent_id, page.addedby, page.modifiedby]).filter(Boolean)
      ),
    ];

    const fetchedUsernames = {};
    await Promise.all(
      userIds.map(async (userId) => {
        try {
          const response = await axios.get(`${BASE_URL}api/user/find-one/${userId}`, { // ✅ Use BASE_URL
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          fetchedUsernames[userId] = response.data.username || "Unknown";
        } catch (error) {
          fetchedUsernames[userId] = "Unknown";
        }
      })
    );

    console.log("Fetched Usernames:", fetchedUsernames); // Log usernames against IDs
    setUsernames(fetchedUsernames);
  };
  return (
    <div className="container mt-4" style={{ fontSize: "0.9rem" }}> {/* Apply smaller font size */}
      <div className="mb-3 d-flex justify-content-between">
        <h2>Pages</h2>
        <a href="add-page" className="btn btn-primary">Add Page</a>
      </div>

      <div className="row mb-4">
        <div className="col-md-1">
          <select className="form-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
          </select>
        </div>
        <div className="col-md-6">
          <input type="text" className="form-control" placeholder="Search by username or email..." value={search} onChange={handleSearch} />
        </div>
      </div>

      <div className="table-responsive">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <Loader message="Loading pages..." />
          </div>
        ) : (
          <table className="table table-hover table-striped align-middle ">
            <thead className="table-dark">
              <tr>
                <th onClick={() => requestSort("pagename")}>Page Name</th>
                <th onClick={() => requestSort("slug")}>Slug</th>
                <th onClick={() => requestSort("category")}>Category</th>
                <th onClick={() => requestSort("category")} >Parent ID</th>
                <th onClick={() => requestSort("addedby")}>Added By</th>
                <th onClick={() => requestSort("modifiedby")}>Modified By</th>
                <th onClick={() => requestSort("addedat")}>Added At</th>
                <th onClick={() => requestSort("modifiedat")}>Modified At</th>
                <th onClick={() => requestSort("status")}>Status</th>
                <th >Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPages.length > 0 ? (
                paginatedPages.map((page) => (
                  <tr key={page.id}>
                    <td>

                      <a href={`/page/${page.slug}`} target="_blank" rel="noopener noreferrer"   >
                        {page.pagename}
                      </a>
                    </td>


                    <td>{page.slug}</td>

                    <td>{page.category || "N/A"}</td>
                    <td>{page.parent_id || "None"}</td>
                    <td>
                      <a
                        href={`/user-detail?id=${page.addedby}`}
                        target="_blank"
                        rel="noopener noreferrer"

                      >
                        {usernames[page.addedby] || "Unknown"}
                      </a>
                    </td>

                    <td>
                      <a href={`/user-detail?id=${page.modifiedby}`} target="_blank" rel="noopener noreferrer"  >
                        {usernames[page.modifiedby] || "N/A"}
                      </a>
                    </td>

                    <td>{page.addedat ? new Date(page.addedat).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' }) : "N/A"}</td>
                    <td>{page.modifiedat ? new Date(page.modifiedat).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' }) : "N/A"}</td>
                    <td>
                      <button className={`btn btn-sm ${page.active ? "btn-danger" : "btn-success"}`} onClick={() => toggleStatus(page.id, page.active)}>
                        {page.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                    <td>
                      <div className="d-flex justify-content-between">
                        <button className="btn btn-sm btn-warning" onClick={() => navigate(`/edit-page?id=${page.id}`)}>Edit</button>
                        <button className="btn btn-sm btn-danger mx-2" onClick={() => deletePage(page.id)}>Delete</button>
                      </div></td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center">No pages found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination */}
      <div className="d-flex justify-content-between">
        <span>Showing {paginatedPages.length} of {filteredPages.length} Pages</span>
        <div>
          <button
            className="btn btn-outline-primary mx-1"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>
          <span className="mx-2">Page {currentPage} of {totalPages}</span>
          <button
            className="btn btn-outline-primary mx-1"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pages;
